import React, { useEffect, useState, useCallback } from "react";
import { Download, Smartphone, Share, Plus, X } from "lucide-react";

/** "Add to Home Screen" prompt.
 *
 *  - Android/Chrome: captures `beforeinstallprompt` and offers the native
 *    install flow with a tap on "Install".
 *  - iOS Safari: shows a short illustrated instruction sheet (Share → Add
 *    to Home Screen) since iOS doesn't expose the install API.
 *  - Hides itself after install or 30 days after a user dismiss.
 *  - Never shows on desktop or when already launched from the home screen.
 */
const DISMISS_KEY = "sv_pwa_dismissed_at";
const DISMISS_DAYS = 30;
const DELAY_MS = 20000; // give users ~20s before nudging

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [platform, setPlatform] = useState(null); // "android" | "ios"

  const dismissedRecently = useCallback(() => {
    try {
      const ts = localStorage.getItem(DISMISS_KEY);
      if (!ts) return false;
      const days = (Date.now() - Number(ts)) / 86400000;
      return days < DISMISS_DAYS;
    } catch (_) { return false; }
  }, []);

  const isStandalone = useCallback(() => {
    try {
      if (window.matchMedia("(display-mode: standalone)").matches) return true;
      // iOS Safari exposes a non-standard flag.
      if (window.navigator && window.navigator.standalone) return true;
    } catch (_) { /* ignore */ }
    return false;
  }, []);

  const isMobile = useCallback(() => {
    const ua = window.navigator.userAgent || "";
    return /iPhone|iPad|iPod|Android/i.test(ua);
  }, []);

  const isIOS = useCallback(() => {
    const ua = window.navigator.userAgent || "";
    // Treat iPadOS (which reports MacIntel + touch) as iOS too.
    return /iPhone|iPad|iPod/i.test(ua) ||
      (ua.includes("Mac") && typeof window.navigator.ontouchend !== "undefined");
  }, []);

  useEffect(() => {
    if (isStandalone() || !isMobile() || dismissedRecently()) return;

    // iOS: no beforeinstallprompt — show instructions after delay.
    if (isIOS()) {
      const t = setTimeout(() => { setPlatform("ios"); setVisible(true); }, DELAY_MS);
      return () => clearTimeout(t);
    }

    // Android/Chrome: wait for the browser's install event.
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setPlatform("android");
      // Delay so the banner isn't the first thing users see.
      setTimeout(() => setVisible(true), DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", handler);
    const onInstalled = () => {
      setVisible(false);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [dismissedRecently, isMobile, isStandalone, isIOS]);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch (_) { /* ignore */ }
    setDeferred(null);
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:bottom-4 sm:left-auto sm:right-4 sm:px-0"
      data-testid="pwa-install-prompt"
    >
      <div className="pointer-events-auto relative mx-auto max-w-sm overflow-hidden rounded-2xl border border-indigo-200 bg-white shadow-xl sm:mx-0 sm:w-80">
        <button
          onClick={dismiss}
          className="absolute right-2 top-2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Dismiss install prompt"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 p-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50">
            <Smartphone className="h-5 w-5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <p className="font-head text-sm font-semibold text-slate-900">Install Servantage</p>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-600">
              Add it to your home screen for faster access, push notifications and a full-screen app experience.
            </p>
          </div>
        </div>

        {platform === "android" && (
          <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50 px-4 py-2.5">
            <button
              onClick={dismiss}
              className="flex-1 rounded-lg py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800"
              data-testid="pwa-install-later"
            >
              Not now
            </button>
            <button
              onClick={install}
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 py-1.5 text-xs font-semibold text-white transition hover:bg-indigo-700"
              data-testid="pwa-install-accept"
            >
              <Download className="h-3.5 w-3.5" />
              Install
            </button>
          </div>
        )}

        {platform === "ios" && (
          <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-[11px] text-slate-600">
            <p className="mb-1.5 font-semibold text-slate-700">On iPhone:</p>
            <ol className="space-y-1 pl-0.5">
              <li className="flex items-center gap-1.5">
                <span className="font-bold text-slate-500">1.</span>
                Tap <Share className="inline h-3.5 w-3.5 text-indigo-600" /> at the bottom
              </li>
              <li className="flex items-center gap-1.5">
                <span className="font-bold text-slate-500">2.</span>
                Choose <Plus className="inline h-3.5 w-3.5 text-indigo-600" /> <span className="font-semibold">Add to Home Screen</span>
              </li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
